import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  MinusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

// These will be populated dynamically from the API
const DEFAULT_APPROVER_TYPES = [
  { key: 'direct_manager', label: 'Direct Manager', description: 'Employee\'s direct manager' },
  { key: 'senior_manager', label: 'Senior Manager', description: 'Manager\'s manager' },
  { key: 'hr_manager', label: 'HR Manager', description: 'HR Manager or Admin' },
  { key: 'department_manager', label: 'Department Manager', description: 'Manager of employee\'s department' },
  { key: 'director', label: 'Director', description: 'Company director' },
  { key: 'role_based', label: 'Role-Based', description: 'Users with specific role' },
  { key: 'specific_user', label: 'Specific User', description: 'Named user' },
  { key: 'multiple', label: 'Multiple Approvers', description: 'Multiple approvers for this step' }
];

const DEFAULT_APPROVAL_MODES = [
  { key: 'sequential', label: 'Sequential', description: 'Approvers must approve one after another' },
  { key: 'parallel', label: 'Parallel', description: 'All approvers can approve simultaneously' },
  { key: 'any_one', label: 'Any One', description: 'Any one approver can approve' },
  { key: 'majority', label: 'Majority', description: 'Majority of approvers must approve' }
];

const WorkflowTemplateModal = ({ 
  isOpen, 
  onClose, 
  template = null, 
  categories = [], 
  availableApprovers = [],
  onSave 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    is_active: true,
    is_default: false,
    template_config: {},
    steps: []
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState(new Set());
  
  // Dynamic dropdown data
  const [dropdownData, setDropdownData] = useState({
    approver_types: DEFAULT_APPROVER_TYPES,
    approval_modes: DEFAULT_APPROVAL_MODES,
    roles: [],
    modules: [],
    entities: {}
  });
  // Dynamic dropdown loading state - not currently used in UI

  // Load dynamic dropdown data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadDropdownData();
    }
  }, [isOpen]);

  const loadDropdownData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Load all dropdown categories in parallel
      const [
        dropdownValuesResponse,
        rolesResponse,
        modulesResponse
      ] = await Promise.all([
        fetch('/api/workflow/dropdown-values', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/centralized-roles/dropdown', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/workflow/modules', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const [dropdownValues, roles, modules] = await Promise.all([
        dropdownValuesResponse.json(),
        rolesResponse.json(),
        modulesResponse.json()
      ]);

      if (dropdownValues.success && roles.success && modules.success) {
        setDropdownData({
          approver_types: dropdownValues.data.approver_types || DEFAULT_APPROVER_TYPES,
          approval_modes: dropdownValues.data.approval_modes || DEFAULT_APPROVAL_MODES,
          escalation_types: dropdownValues.data.escalation_types || [],
          priority_levels: dropdownValues.data.priority_levels || [],
          notification_types: dropdownValues.data.notification_types || [],
          action_types: dropdownValues.data.action_types || [],
          roles: roles.data || [],
          modules: modules.data || [],
          entities: {}
        });
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  };

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        description: template.description || '',
        category_id: template.category_id || '',
        is_active: template.is_active ?? true,
        is_default: template.is_default ?? false,
        template_config: template.template_config || {},
        steps: template.steps || []
      });
      // Expand all steps for editing
      setExpandedSteps(new Set(template.steps?.map((_, index) => index) || []));
    } else {
      // Reset form for new template
      setFormData({
        name: '',
        description: '',
        category_id: categories[0]?.id || '',
        is_active: true,
        is_default: false,
        template_config: {},
        steps: [{
          step_name: 'Manager Review',
          approver_type: 'direct_manager',
          approval_mode: 'sequential',
          required_approvers: 1,
          is_required: true,
          escalation_days: 3,
          conditions: {},
          approver_config: {},
          approvers: []
        }]
      });
      setExpandedSteps(new Set([0]));
    }
    setErrors({});
  }, [template, categories, isOpen]);

  const addStep = () => {
    const newStep = {
      step_name: `Step ${formData.steps.length + 1}`,
      approver_type: 'direct_manager',
      approval_mode: 'sequential',
      required_approvers: 1,
      is_required: true,
      escalation_days: 3,
      conditions: {},
      approver_config: {},
      approvers: []
    };
    
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, newStep]
    }));
    
    // Expand the new step
    setExpandedSteps(prev => new Set([...prev, formData.steps.length]));
  };

  const removeStep = (index) => {
    if (formData.steps.length <= 1) {
      alert('At least one step is required');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
    
    // Update expanded steps
    setExpandedSteps(prev => {
      const newSet = new Set();
      prev.forEach(stepIndex => {
        if (stepIndex < index) {
          newSet.add(stepIndex);
        } else if (stepIndex > index) {
          newSet.add(stepIndex - 1);
        }
      });
      return newSet;
    });
  };

  const updateStep = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const moveStep = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.steps.length) return;

    setFormData(prev => {
      const newSteps = [...prev.steps];
      [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
      return { ...prev, steps: newSteps };
    });

    // Update expanded steps
    setExpandedSteps(prev => {
      const newSet = new Set();
      prev.forEach(stepIndex => {
        if (stepIndex === index) {
          newSet.add(newIndex);
        } else if (stepIndex === newIndex) {
          newSet.add(index);
        } else {
          newSet.add(stepIndex);
        }
      });
      return newSet;
    });
  };

  const toggleStepExpansion = (index) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const addApproverToStep = (stepIndex) => {
    const newApprover = {
      approver_type: 'specific_user',
      approver_user_id: '',
      is_primary: false,
      sort_order: formData.steps[stepIndex].approvers?.length + 1 || 1
    };
    
    updateStep(stepIndex, 'approvers', [
      ...(formData.steps[stepIndex].approvers || []),
      newApprover
    ]);
  };

  const removeApproverFromStep = (stepIndex, approverIndex) => {
    const step = formData.steps[stepIndex];
    const newApprovers = step.approvers?.filter((_, i) => i !== approverIndex) || [];
    updateStep(stepIndex, 'approvers', newApprovers);
  };

  const updateStepApprover = (stepIndex, approverIndex, field, value) => {
    const step = formData.steps[stepIndex];
    const newApprovers = step.approvers?.map((approver, i) => 
      i === approverIndex ? { ...approver, [field]: value } : approver
    ) || [];
    updateStep(stepIndex, 'approvers', newApprovers);
  };

  const validateForm = () => {
    const newErrors = {};

    // Template validation
    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }
    
    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    // Steps validation
    formData.steps.forEach((step, index) => {
      if (!step.step_name.trim()) {
        newErrors[`step_${index}_name`] = 'Step name is required';
      }
      
      if (step.approver_type === 'role_based' && !step.approver_role) {
        newErrors[`step_${index}_role`] = 'Role is required for role-based approval';
      }
      
      if (step.approver_type === 'specific_user' && !step.approver_user_id) {
        newErrors[`step_${index}_user`] = 'User is required for specific user approval';
      }
      
      if (step.approver_type === 'multiple' && (!step.approvers || step.approvers.length === 0)) {
        newErrors[`step_${index}_approvers`] = 'At least one approver is required for multiple approver steps';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const success = await onSave(formData);
      if (success) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {template ? 'Edit Workflow Template' : 'Create Workflow Template'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Template Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter template name"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.category_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.category_id && <p className="text-red-500 text-sm mt-1">{errors.category_id}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter template description"
              />
            </div>

            {/* Template Settings */}
            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Active</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_default}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Set as Default</span>
              </label>
            </div>

            {/* Workflow Steps */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Workflow Steps</h3>
                <button
                  type="button"
                  onClick={addStep}
                  className="flex items-center space-x-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Add Step</span>
                </button>
              </div>

              <div className="space-y-4">
                {formData.steps.map((step, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg">
                    {/* Step Header */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-t-lg">
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={() => toggleStepExpansion(index)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          {expandedSteps.has(index) ? (
                            <ChevronUpIcon className="h-4 w-4" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4" />
                          )}
                        </button>
                        <span className="text-sm font-medium text-gray-700">
                          Step {index + 1}: {step.step_name || 'Unnamed Step'}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({dropdownData.approver_types.find(t => t.key === step.approver_type)?.label || step.approver_type})
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => moveStep(index, 'up')}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="Move Up"
                          >
                            <ChevronUpIcon className="h-4 w-4" />
                          </button>
                        )}
                        {index < formData.steps.length - 1 && (
                          <button
                            type="button"
                            onClick={() => moveStep(index, 'down')}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="Move Down"
                          >
                            <ChevronDownIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeStep(index)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Remove Step"
                          disabled={formData.steps.length <= 1}
                        >
                          <MinusIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Step Content */}
                    {expandedSteps.has(index) && (
                      <div className="p-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Step Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={step.step_name}
                              onChange={(e) => updateStep(index, 'step_name', e.target.value)}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors[`step_${index}_name`] ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="Enter step name"
                            />
                            {errors[`step_${index}_name`] && (
                              <p className="text-red-500 text-sm mt-1">{errors[`step_${index}_name`]}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Approver Type
                            </label>
                            <select
                              value={step.approver_type}
                              onChange={(e) => updateStep(index, 'approver_type', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {dropdownData.approver_types.map(type => (
                                <option key={type.key} value={type.key}>
                                  {type.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Specific Approver Configuration */}
                        {step.approver_type === 'role_based' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Role <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={step.approver_role || ''}
                              onChange={(e) => updateStep(index, 'approver_role', e.target.value)}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors[`step_${index}_role`] ? 'border-red-500' : 'border-gray-300'
                              }`}
                            >
                              <option value="">Select role</option>
                              {dropdownData.roles.map(role => (
                                <option key={role.role_key} value={role.role_key}>
                                  {role.label} {role.hierarchy_level ? `(Level ${role.hierarchy_level})` : ''}
                                </option>
                              ))}
                            </select>
                            {errors[`step_${index}_role`] && (
                              <p className="text-red-500 text-sm mt-1">{errors[`step_${index}_role`]}</p>
                            )}
                          </div>
                        )}

                        {step.approver_type === 'specific_user' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              User <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={step.approver_user_id || ''}
                              onChange={(e) => updateStep(index, 'approver_user_id', e.target.value)}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors[`step_${index}_user`] ? 'border-red-500' : 'border-gray-300'
                              }`}
                            >
                              <option value="">Select user</option>
                              {availableApprovers.map(user => (
                                <option key={user.id} value={user.id}>
                                  {user.first_name} {user.last_name} ({user.email})
                                </option>
                              ))}
                            </select>
                            {errors[`step_${index}_user`] && (
                              <p className="text-red-500 text-sm mt-1">{errors[`step_${index}_user`]}</p>
                            )}
                          </div>
                        )}

                        {/* Multiple Approvers */}
                        {step.approver_type === 'multiple' && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Approvers <span className="text-red-500">*</span>
                              </label>
                              <button
                                type="button"
                                onClick={() => addApproverToStep(index)}
                                className="text-sm text-blue-600 hover:text-blue-800"
                              >
                                + Add Approver
                              </button>
                            </div>
                            
                            {step.approvers?.map((approver, approverIndex) => (
                              <div key={approverIndex} className="flex items-center space-x-2 mb-2">
                                <select
                                  value={approver.approver_user_id || ''}
                                  onChange={(e) => updateStepApprover(index, approverIndex, 'approver_user_id', e.target.value)}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="">Select user</option>
                                  {availableApprovers.map(user => (
                                    <option key={user.id} value={user.id}>
                                      {user.first_name} {user.last_name} ({user.email})
                                    </option>
                                  ))}
                                </select>
                                <label className="flex items-center text-sm">
                                  <input
                                    type="checkbox"
                                    checked={approver.is_primary || false}
                                    onChange={(e) => updateStepApprover(index, approverIndex, 'is_primary', e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="ml-1">Primary</span>
                                </label>
                                <button
                                  type="button"
                                  onClick={() => removeApproverFromStep(index, approverIndex)}
                                  className="p-1 text-red-600 hover:text-red-800"
                                >
                                  <MinusIcon className="h-4 w-4" />
                                </button>
                              </div>
                            )) || []}
                            
                            {errors[`step_${index}_approvers`] && (
                              <p className="text-red-500 text-sm mt-1">{errors[`step_${index}_approvers`]}</p>
                            )}
                          </div>
                        )}

                        {/* Approval Settings */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Approval Mode
                            </label>
                            <select
                              value={step.approval_mode}
                              onChange={(e) => updateStep(index, 'approval_mode', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {dropdownData.approval_modes.map(mode => (
                                <option key={mode.key} value={mode.key}>
                                  {mode.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Escalation Days
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="30"
                              value={step.escalation_days}
                              onChange={(e) => updateStep(index, 'escalation_days', parseInt(e.target.value) || 3)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>

                          <div className="flex items-center space-x-4 pt-6">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={step.is_required}
                                onChange={(e) => updateStep(index, 'is_required', e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">Required</span>
                            </label>
                          </div>
                        </div>

                        {/* Step Info */}
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                          <div className="flex">
                            <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5" />
                            <div className="ml-3">
                              <p className="text-sm text-blue-700">
                                {dropdownData.approver_types.find(t => t.key === step.approver_type)?.description || 'Approver type description'}
                              </p>
                              {step.approval_mode !== 'sequential' && (
                                <p className="text-xs text-blue-600 mt-1">
                                  {dropdownData.approval_modes.find(m => m.key === step.approval_mode)?.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
            <span>{template ? 'Update Template' : 'Create Template'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowTemplateModal;